import {
  LogsInitConfiguration,
  Logger as DatadogLoggerInstance,
  datadogLogs,
} from '@datadog/browser-logs';
import { v4 as uuidv4 } from 'uuid';
import { AbstractLogger } from './abstract-logger';
import { ILogger, LogLevel, LoggerType } from './logger.types';
import { UnreachableCaseError } from 'ts-essentials';

export class DatadogLogger extends AbstractLogger {
  private readonly logger?: DatadogLoggerInstance;
  private static isInitialized = false;

  public override get type(): LoggerType {
    return 'datadog';
  }

  constructor(
    name = 'datadog',
    logLevel: LogLevel,
    private readonly config: LogsInitConfiguration
  ) {
    super(name, logLevel);

    if (logLevel === LogLevel.None) return;

    if (!DatadogLogger.isInitialized) {
      this.initialize(config);
      DatadogLogger.isInitialized = true;
    }

    this.logger = datadogLogs.createLogger(name);
  }

  public createNamedLogger(name: string): ILogger {
    return new DatadogLogger(name, this.level, this.config);
  }

  private initialize(config: LogsInitConfiguration): void {
    datadogLogs.init({
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error'],
      forwardReports: 'all',
      sessionSampleRate: 100,
      storeContextsAcrossPages: true,
      beforeSend: (_event, context) => {
        // Ignore aborted requests (caused by user or browser)
        if (context && 'isAborted' in context && context.isAborted) {
          return false;
        }
        return true;
      },
      ...config,
    });

    datadogLogs.setGlobalContextProperty('session', uuidv4());
    datadogLogs.setGlobalContextProperty('env', config.env);
    datadogLogs.removeGlobalContextProperty('project');
  }

  public setProjectContext(project?: {
    id: string;
    name: string;
    location: string;
  }): void {
    if (!this.logger) return;

    if (!project) {
      datadogLogs.removeGlobalContextProperty('project');
      return;
    }

    datadogLogs.setGlobalContextProperty('project', {
      id: project.id,
      name: project.name,
      location: project.location,
    });
  }

  protected log(
    level: LogLevel,
    message: string,
    context?: object,
    error?: Error
  ): void {
    if (!this.shouldLog(level) || !this.logger) return;
    const loggerMessage = `[${this.name}]: ${message}`;

    switch (level) {
      case LogLevel.Debug:
        return this.logger.debug(loggerMessage, context, error);

      case LogLevel.Info:
        return this.logger.info(loggerMessage, context, error);

      case LogLevel.Warn:
        return this.logger.warn(loggerMessage, context, error);

      case LogLevel.Error:
        return this.logger.error(loggerMessage, context, error);

      default:
        throw new UnreachableCaseError(level);
    }
  }
}
